// "Opomniki" (every signed-in user): done reminders can be deleted automatically after a chosen
// period. The user's own preference, saved on the server right away (online only).
import { useState } from 'react';
import type { AutoDeleteDoneNotesDays, User } from '../../../../shared/types';
import { ApiClientError } from '../../lib/http';
import { accountErrorText } from '../../components/AccountForms';
import { confirmDialog } from '../../components/Dialog';
import { toast } from '../../components/Toasts';
import { LoadError, Loading, useLoaded } from './common';
import { AUTO_DELETE_CHOICES, autoDeleteLabel, loadPreferences, savePreferences } from './preferences';

export const MSG_PREFERENCES_OFFLINE =
  'Ni povezave s strežnikom, zato nastavitve ni bilo mogoče shraniti. ' +
  'Shrani se le, ko je povezava na voljo – poskusite znova pozneje.';

function confirmText(days: AutoDeleteDoneNotesDays): string {
  return (
    `Opravljeni opomniki se bodo izbrisali ${autoDeleteLabel(days)} od trenutka, ko ste jih odkljukali; ` +
    'tisti, ki so opravljeni že dlje, v naslednji uri. Izbrisanih opomnikov (besedila in posnetka) ni mogoče obnoviti.'
  );
}

function parseChoice(value: string): AutoDeleteDoneNotesDays | null {
  return AUTO_DELETE_CHOICES.find((c) => String(c.value ?? '') === value)?.value ?? null;
}

export function RemindersSection({ user }: { user: Pick<User, 'id'> }) {
  const loaded = useLoaded(() => loadPreferences(user.id), 'Nastavitve opomnikov ni bilo mogoče naložiti.');
  /** The choice being confirmed or saved (shown meanwhile). */
  const [pending, setPending] = useState<{ value: AutoDeleteDoneNotesDays | null } | null>(null);
  const data = loaded.data;
  const current = data?.autoDeleteDoneNotesDays ?? null;
  const shown = pending ? pending.value : current;

  const change = async (next: AutoDeleteDoneNotesDays | null) => {
    if (!data || pending || next === current) return;
    setPending({ value: next });
    try {
      // Switching on or shortening the period can delete reminders within the hour.
      if (next !== null && (current === null || next < current)) {
        const ok = await confirmDialog({
          title: `Opravljene opomnike izbrisati ${autoDeleteLabel(next)}?`,
          message: confirmText(next),
          confirmLabel: current === null ? 'Vklopi brisanje' : 'Skrajšaj čas',
          danger: true,
        });
        if (!ok) return;
      }
      const saved = await savePreferences(user.id, { autoDeleteDoneNotesDays: next });
      loaded.set(saved);
      const days = saved.autoDeleteDoneNotesDays;
      toast(
        days === null
          ? 'Opravljeni opomniki se ne bodo samodejno brisali.'
          : `Opravljeni opomniki se bodo samodejno izbrisali ${autoDeleteLabel(days)}.`,
        { kind: 'success' },
      );
    } catch (err) {
      const offline = err instanceof ApiClientError && err.status === 0;
      toast(offline ? MSG_PREFERENCES_OFFLINE : accountErrorText(err, 'Nastavitve ni bilo mogoče shraniti.'), { kind: 'error' });
    } finally {
      setPending(null);
    }
  };

  return (
    <section className="card" aria-labelledby="reminders-title" id="opomniki">
      <h2 id="reminders-title" className="card__title">
        Opomniki
      </h2>
      {loaded.error && !data ? (
        <LoadError message={loaded.error} onRetry={loaded.reload} />
      ) : !data ? (
        <Loading />
      ) : (
        <>
          <div className="field">
            <label htmlFor="auto-delete-done">Opravljene opomnike samodejno izbriši</label>
            <select
              id="auto-delete-done"
              value={String(shown ?? '')}
              disabled={pending !== null}
              aria-describedby="auto-delete-done-hint"
              onChange={(e) => void change(parseChoice(e.target.value))}
            >
              {AUTO_DELETE_CHOICES.map((c) => (
                <option key={c.label} value={String(c.value ?? '')}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>
          <p id="auto-delete-done-hint" className="muted small">
            Čas se šteje od trenutka, ko opomnik odkljukate kot opravljen. Opomnik se izbriše skupaj z besedilom in posnetkom in ga ni
            mogoče obnoviti. Velja samo za vaše opomnike; sestanki in nadzori se nikoli ne brišejo samodejno.
          </p>
        </>
      )}
    </section>
  );
}
